<template>
  <div class="terms-letter">
    <a-terms-search-result
      :key="key"
      :title="searchTitle"
      :term-list="termList"
      :term-count="TotalCount"
      :page-count="PagesCount"
      :in-progress="inProgress"
      @request-terms="getTermsList"
    />

    <a-banner
      :banner-settings="BANNER_SETTINGS.TERMS_LETTER.BOTTOM_BANNER"
      class="terms-letter__banner"
    />

    <a-sticky-banner
      :sticky-position="STICKY_BANNER_POSITION.BOTTOM"
      :banner-settings="BANNER_SETTINGS.TERMS_LETTER.STICKY_TO_BOTTOM"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import ATermsSearchResult from '@/components/_sections/terms/ATermsSearchResult'
import { LETTERS } from '@/components/_sections/terms/ALetterNavigation'
import { TERM_LIST_MODEL } from 'enums/responseModels'
import ABanner from 'shared/ABanner'
import { BANNER_SETTINGS } from 'enums/banners/banner-settings'
import AStickyBanner from 'shared/AStickyBanner'
import { STICKY_BANNER_POSITION } from 'enums/banners/sticky-banner'
import { PAGE_SCHEME_TYPE } from 'enums/pageSchemes'
import { checkIfNonCanonicalPath } from '@/utils/helpers/canonical-links'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import { generateFmUrl } from '@/utils/helpers/url-generators'

export const SEARCH_TERM_TITLE_BASE = 'Terms beginning with '

export default {
  name: 'TermLetter',
  components: { AStickyBanner, ABanner, ATermsSearchResult },
  head() {
    return this.$generateMetaTags({
      titleParam: this.searchTitle,
      descriptionParam: this.searchTitle,
      schemes: [
        { type: PAGE_SCHEME_TYPE.WEB_SITE },
        { type: PAGE_SCHEME_TYPE.ORGANIZATION }
      ],
      canonicalUrl: this.canonicalUrl
    })
  },
  async asyncData(ctx) {
    const { path: currentPath, fullPath } = ctx.route
    const { letter } = ctx.route.params

    try {
      const dispatch = ctx.store.dispatch
      const applyFallback = ctx.app.$helper.applyFallback

      if (!LETTERS.includes(letter)) throw new Error('invalid letter')

      const canonicalUrl = generateFmUrl(`/terms/${letter.toLowerCase()}/`, ctx)

      if (checkIfNonCanonicalPath(ctx, { currentPath, canonicalUrl })) {
        redirectHandler({ ctx, to: canonicalUrl })
        return
      }

      const { Terms, TotalCount, PagesCount } = await dispatch(
        'terms/requestTermsByLetter',
        {
          queryParams: {
            letter,
            take: 20,
            page: 0
          }
        }
      )
      return {
        canonicalUrl,
        termList: applyFallback(Terms, TERM_LIST_MODEL),
        key: ctx.app.$helper.guid(),
        letter,
        TotalCount,
        PagesCount,
        inProgress: false
      }
    } catch (err) {
      ctx.app.$errorHandler(err, ctx, {
        showErrorPage: true,
        showMessage: false
      })
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  data() {
    return {
      canonicalUrl: this.$route.path,
      BANNER_SETTINGS,
      STICKY_BANNER_POSITION
    }
  },
  computed: {
    searchTitle() {
      return `${SEARCH_TERM_TITLE_BASE}"${this.letter.toUpperCase()}"`
    }
  },
  methods: {
    ...mapActions({
      requestTermsByLetter: 'terms/requestTermsByLetter'
    }),
    async getTermsList({ take, page }) {
      this.inProgress = true
      try {
        const {
          Terms,
          TotalCount,
          PagesCount
        } = await this.requestTermsByLetter({
          queryParams: {
            take,
            page,
            letter: this.letter
          }
        })
        this.termList = [
          ...this.termList,
          ...this.$helper.applyFallback(Terms, TERM_LIST_MODEL)
        ]
        this.TotalCount = TotalCount
        this.PagesCount = PagesCount
      } catch (err) {
        this.$errorHandler(err, this, {
          showPageError: true,
          showMessage: false
        })
      } finally {
        this.inProgress = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.terms-letter {
  .terms-letter__banner {
    margin-top: 40px;
    margin-bottom: 40px;

    @include mobile {
      margin-top: 30px;
      margin-bottom: 30px;
    }
  }
}
</style>
